import { useRef } from 'react';
import { createDocValueStore } from './docStore';
import { cancelCalls } from './stoppableCalls';

// "Stop what you are doing" for every phase of one document — the whole of what the
// Pause button does.
//
// A counter rather than an AbortController, and that difference IS the feature.
// Aborting a request does not cancel the call behind it: the Flask route runs to the
// end and Gemini bills it whether or not anyone is still listening. So an abort buys
// nothing except the guarantee that an answer already paid for arrives at a closed
// socket and is thrown away. You pay and you get nothing.
//
// So nothing aborts any more. Each phase captures the epoch when it starts and
// checks it BEFORE dispatching the next call: new work stops at once — which is the
// only real saving available — and whatever was already in flight lands and is kept,
// because it is already bought.
//
// A phase halted this way is PARTIAL, which is why the resume point names the phase
// itself rather than the one after it, and why every phase can pick its own work back
// up item by item (see runAll, and the `resume` argument each phase takes).
//
// An epoch and not a flag, so a fresh run needs no reset: a run captures the current
// value, so a halt asked for during the previous one cannot leak into it.
const epochStore = createDocValueStore<number>(0);

// Ask every phase of this document to stop issuing new work.
export function haltRuns(docId: string) {
  epochStore.set(docId, epochStore.get(docId) + 1);
}

// STOP, AS THE STOP BUTTONS MEAN IT (Louis 2026-11-03): no new calls, AND the ones out
// cancelled at OpenAI — see stoppableCalls. What is written above about aborting is true of a
// request dropped by the page and of Gemini; it is not true of a background call at OpenAI,
// which the server cancels where it stands. Pause (haltRuns alone) still lets them land.
export function stopRuns(docId: string) {
  haltRuns(docId);
  cancelCalls();
}

// WHETHER A STOP HAS BEEN ASKED OF THE RUN THAT IS OUT — for the button to say so at once
// ("Stopping…") rather than look as if the press did nothing while the calls wind down.
// The epoch when the run was first seen busy, and whether it has moved since.
export function useStopAsked(docId: string, busy: boolean): boolean {
  const epoch = epochStore.useValue(docId);
  const from = useRef<number | null>(null);
  if (!busy) from.current = null;
  else if (from.current == null) from.current = epoch;
  return busy && epoch !== from.current;
}

// Call once at the start of a phase; the result answers "has a halt been asked for
// since?". Check it BEFORE each unit of work and never after — a check after the
// await is how an answer you were billed for gets dropped on the floor.
export function haltGate(docId: string): () => boolean {
  const at = epochStore.get(docId);
  return () => epochStore.get(docId) !== at;
}
